import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly DEFAULT_DURATION_MS = 3500;

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Primary notification method.
   * type defaults to 'info'; duration defaults to 3500ms for success/info, 5000ms for error/warning.
   */
  notify(
    message: string,
    type: NotificationType = 'info',
    duration?: number
  ): void {
    const durationMs =
      duration ?? (type === 'error' || type === 'warning' ? 5000 : this.DEFAULT_DURATION_MS);

    this.snackBar.open(message, 'Dismiss', {
      duration: durationMs,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });
  }

  success(message: string, duration?: number): void {
    this.notify(message, 'success', duration);
  }

  error(message: string, duration?: number): void {
    this.notify(message, 'error', duration);
  }

  warning(message: string, duration?: number): void {
    this.notify(message, 'warning', duration);
  }

  info(message: string, duration?: number): void {
    this.notify(message, 'info', duration);
  }

  /**
   * Confirmation via snackbar action button.
   * Resolves true when the action button is clicked, false when dismissed.
   */
  confirm(message: string, actionLabel: string = 'Confirm'): Promise<boolean> {
    const ref = this.snackBar.open(message, actionLabel, {
      duration: 8000,
      panelClass: ['snackbar-confirm'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    return new Promise(resolve => {
      ref.onAction().subscribe(() => resolve(true));
      ref.afterDismissed().subscribe(({ dismissedByAction }) => {
        if (!dismissedByAction) resolve(false);
      });
    });
  }
}
